import type { InjectionKey, Ref } from 'vue'
import { doc, getDoc, type Firestore } from 'firebase/firestore'

export const SHIPMENT_COMPLETED_STATUS = 'COMPLETED'
export const SHIPMENT_COMPLETED_TRIGGER = 'isShipmentCompleted'
export type CompletionTrigger = 'MANUAL_USER' | 'AUTO_ATA' | 'AUTO_CONTAINER_RETURN' | 'AUTO_MAX_DAYS_EXCEEDED' | 'AUTO_INVALID_REFERENCE' | 'AUTO_STATUS'

/** Quien ejecuta un cambio de ciclo de vida (cierre o reactivación). */
export interface ShipmentActor { uid: string; email?: string | null; name?: string; lastName?: string }

/** Embarque en solo lectura. Mismo predicado que la CF (shipment-lifecycle/services/status-history.ts). */
export function isShipmentLocked(
  shipment: { isShipmentCompleted?: unknown; status?: unknown } | null | undefined
): boolean {
  return shipment?.isShipmentCompleted === true || shipment?.status === SHIPMENT_COMPLETED_STATUS
}

/** Solo admin (o superadmin interno de Kila) reactiva, y solo si el embarque está bloqueado. `role` debe ser el rol RESUELTO (authStore.userRole), nunca user.role crudo. */
export function canReactivateShipment(
  shipment: { isShipmentCompleted?: unknown; status?: unknown } | null | undefined,
  role: string | null | undefined
): boolean {
  return isShipmentLocked(shipment) && (role === 'admin' || role === 'superadmin')
}

export class ShipmentLockedError extends Error {
  constructor(public shipmentId: string) {
    super(`SHIPMENT_LOCKED: el embarque ${shipmentId} está completado y es de solo lectura`)
    this.name = 'ShipmentLockedError'
  }
}

/** Lee el embarque y lanza ShipmentLockedError si está completado. Si el doc no existe, no lanza (el llamador ya maneja "no encontrado"). */
export async function assertShipmentWritable(db: Firestore, companyId: string, shipmentId: string): Promise<void> {
  const snap = await getDoc(doc(db, 'Companies', companyId, 'shipments', shipmentId))
  if (!snap.exists()) return
  if (isShipmentLocked(snap.data())) throw new ShipmentLockedError(shipmentId)
}

/** Lo provee TrackingResults; lo lee DynamicField. */
export const SHIPMENT_LOCKED_KEY: InjectionKey<Ref<boolean>> = Symbol('shipmentLocked')
